import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './TransferScreen.css';
import { getStoredChatId } from './chatId';
import {
  useContacts, contactPickerSupported, pickAndSaveContacts, setContactFavorite,
} from './contactsStore';
import { findUserByCard } from './cardLookup';
import AmountScreen from './AmountScreen';
import TransferSuccessScreen from './TransferSuccessScreen';
import { OwnTransfer } from './BuyCurrencyScreen';

const CLOSE_MS = 320; // синхронізовано з анімацією закриття в TransferScreen.css
const CARD_LEN = 16;

// Введення схоже на номер картки: лише цифри/пробіли, перша 2–6
// (телефони починаються з +, 0 або 38 — їх шукаємо серед контактів)
const isCardInput = (v) => /^[\d ]+$/.test(v) && /^[2-6]/.test(v) && !/^38/.test(v);
const formatCard = (digits) => digits.slice(0, CARD_LEN).replace(/(\d{4})(?=\d)/g, '$1 ');

// «На мою …» — знахідний відмінок назви картки
const CARD_ACC = {
  black: 'Чорну', white: 'Білу', usd: 'доларову', eur: 'єврову', cashback: 'Кешбек',
  kids: 'Дитячу', diia: 'Дія.Картку', evidnovlennia: 'єВідновлення',
};

// Фон кружка без фото — стабільний колір від імені
const AVATAR_COLORS = ['#9c27b0', '#3f51b5', '#009688', '#e91e63', '#ff7043', '#5c6bc0', '#26a69a'];
function avatarColor(name) {
  let h = 0;
  for (const ch of name || '') h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function Avatar({ contact, size }) {
  return (
    <div className={`trs-avatar trs-avatar--${size}`}>
      {contact.photo ? (
        <img className="trs-avatar-img" src={contact.photo} alt="" />
      ) : (
        <div className="trs-avatar-img" style={{ backgroundColor: avatarColor(contact.name) }}>
          <svg viewBox="0 0 24 24" className="trs-avatar-person">
            <circle cx="12" cy="8" r="4.2" fill="#fff" />
            <path d="M4 21c0-4.4 3.6-7.2 8-7.2s8 2.8 8 7.2z" fill="#fff" />
          </svg>
        </div>
      )}
      <span className="trs-avatar-badge">m</span>
    </div>
  );
}

function Star({ filled }) {
  return (
    <svg viewBox="0 0 24 24" className="trs-star-svg">
      <path
        d="M12 2.8l2.8 5.8 6.3.9-4.6 4.4 1.1 6.3L12 17.2l-5.6 3 1.1-6.3L2.9 9.5l6.3-.9z"
        fill={filled ? '#e0e0e0' : 'none'}
        stroke="#e0e0e0"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Екран «Переказ на картку» — виїжджає справа, як «Відкрити картку».
// cards — усі картки користувача (основна теж), fromCardId — з якої
// натиснули «Переказати на картку»: її в «На мою …» не показуємо.
export default function TransferScreen({
  cards = [], fromCardId, fxSpent = 0, onClose,
}) {
  const chatId = getStoredChatId();
  const contacts = useContacts(chatId);
  const [closing, setClosing] = useState(false);
  const [query, setQuery] = useState('');
  const [picking, setPicking] = useState(false);
  // Отримувач за номером картки: undefined — шукаємо, null — не знайдено
  const [cardMatch, setCardMatch] = useState(undefined);
  // Відкритий екран суми: { recipient, cardNumber }
  const [amountFor, setAmountFor] = useState(null);
  // Надісланий переказ (результат sendTransfer) — екран «Платіж надіслано»
  const [sent, setSent] = useState(null);
  // «На мою …» — переказ на свою картку (OwnTransfer)
  const [ownTarget, setOwnTarget] = useState(null);
  const timerRef = useRef(null);
  const pickerAvailable = contactPickerSupported();

  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Смуга під home-індикатором фарбується фоном body — підміняємо на час екрана
  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#121212';
    return () => { document.body.style.backgroundColor = prev; };
  }, []);

  // Висота клавіатури: у standalone-PWA на iOS вона перекриває екран, не
  // зменшуючи його, тож кнопку піднімаємо самі по visualViewport
  const [keyboardInset, setKeyboardInset] = useState(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const update = () => setKeyboardInset(Math.max(0, window.innerHeight - vv.height - vv.offsetTop));
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  // Сума вводиться власною клавіатурою екрана суми — системну ховаємо
  function openAmount(recipient, cardNumber = cardDigits) {
    if (document.activeElement) document.activeElement.blur();
    setAmountFor({ recipient, cardNumber });
  }

  // Збережена картка («Зберегти картку») — одразу на екран суми
  const openContact = (c) => (c.card
    ? () => openAmount(c.recipient ? { ...c.recipient, name: c.name, photo: c.photo } : null, c.card)
    : undefined);

  function close() {
    if (closing) return;
    setClosing(true);
    timerRef.current = setTimeout(onClose, CLOSE_MS);
  }

  async function addContacts() {
    if (picking) return;
    setPicking(true);
    try {
      await pickAndSaveContacts(chatId, contacts);
    } catch (e) {
      // скасування пікера або заборона доступу — нічого не показуємо
    } finally {
      setPicking(false);
    }
  }

  // Номер картки показуємо групами по 4 і обрізаємо до 16 цифр
  function handleQueryChange(e) {
    const v = e.target.value;
    setQuery(isCardInput(v) ? formatCard(v.replace(/\D/g, '')) : v);
  }

  const cardDigits = isCardInput(query) ? query.replace(/\D/g, '') : '';
  const cardComplete = cardDigits.length === CARD_LEN;

  useEffect(() => {
    if (!cardComplete) return undefined;
    let cancelled = false;
    setCardMatch(undefined);
    findUserByCard(cardDigits)
      .then((found) => { if (!cancelled) setCardMatch(found); })
      .catch(() => { if (!cancelled) setCardMatch(null); });
    return () => { cancelled = true; };
  }, [cardComplete, cardDigits]);

  const sorted = useMemo(
    () => [...contacts].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'uk')),
    [contacts],
  );
  const q = query.trim().toLowerCase();
  const qDigits = q.replace(/\D/g, '');
  const filtered = q
    ? sorted.filter((c) => (c.name || '').toLowerCase().includes(q)
      || (qDigits && (c.tel || '').replace(/\D/g, '').includes(qDigits)))
    : sorted;
  const favorites = sorted.filter((c) => c.favorite);
  const ownCards = cards.filter((c) => c.id !== fromCardId);
  const showContacts = !cardDigits && (filtered.length > 0 || (!q && pickerAvailable));

  return createPortal((
    <div className={`trs-root ${closing ? 'closing' : ''}`}>
      <div className="trs-screen">
        <div className="trs-header">
          <div className="trs-topbar">
            <button type="button" className="trs-icon-btn" onClick={close} aria-label="Назад">
              <svg viewBox="0 0 20 16" className="trs-back-svg">
                <path d="M19 8H1.5M8 1.2L1.2 8L8 14.8" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button type="button" className="trs-icon-btn" aria-label="Архів">
              <svg viewBox="0 0 20 18" className="trs-archive-svg">
                <rect x="0.5" y="0" width="19" height="4" rx="1" fill="#fff" />
                <path d="M1.8 5.6h16.4v9.6a2.6 2.6 0 0 1-2.6 2.6H4.4a2.6 2.6 0 0 1-2.6-2.6z" fill="#fff" />
                <rect x="7" y="8" width="6" height="1.8" rx="0.9" fill="#3b4fc0" />
              </svg>
            </button>
          </div>
          <h1 className="trs-title">Переказ на картку</h1>
          <label className="trs-search">
            <input
              className="trs-search-input"
              type="text"
              inputMode="tel"
              autoComplete="off"
              placeholder="Уведіть ім’я, номер картки або телефону"
              value={query}
              onChange={handleQueryChange}
            />
            {query ? (
              <button
                type="button"
                className="trs-clear"
                aria-label="Очистити"
                onPointerDown={(e) => e.preventDefault()}
                onClick={() => setQuery('')}
              >
                <svg viewBox="0 0 20 20">
                  <path d="M2 2l16 16M18 2L2 18" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </button>
            ) : (
            <svg viewBox="0 0 24 24" className="trs-scan-svg">
              <path d="M3 8V5.5A2.5 2.5 0 0 1 5.5 3H8M16 3h2.5A2.5 2.5 0 0 1 21 5.5V8M21 16v2.5a2.5 2.5 0 0 1-2.5 2.5H16M8 21H5.5A2.5 2.5 0 0 1 3 18.5V16" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
              <path d="M2.5 12h19" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
              <path d="M7 9.5V8.5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v1M7 14.5v1a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            )}
          </label>
        </div>

        {!q && (
          <>
            <div className="trs-block trs-group">
              <div className="trs-group-icon">
                <svg viewBox="0 0 24 24">
                  <circle cx="12" cy="8.2" r="2.6" fill="#fff" />
                  <circle cx="6" cy="10" r="2" fill="#fff" />
                  <circle cx="18" cy="10" r="2" fill="#fff" />
                  <path d="M7.6 17.5c0-2.6 2-4.4 4.4-4.4s4.4 1.8 4.4 4.4c0 .8-.6 1.3-1.3 1.3H8.9c-.7 0-1.3-.5-1.3-1.3z" fill="#fff" />
                  <path d="M2.6 16c0-2 1.5-3.4 3.4-3.4.6 0 1.1.1 1.6.4-.8.9-1.3 2-1.4 3.3-.1.6-.6 1-1.2 1H3.6c-.6 0-1-.5-1-1.3zM21.4 16c0-2-1.5-3.4-3.4-3.4-.6 0-1.1.1-1.6.4.8.9 1.3 2 1.4 3.3.1.6.6 1 1.2 1h1.4c.6 0 1-.5 1-1.3z" fill="#fff" />
                </svg>
              </div>
              <div className="trs-group-text">
                <p className="trs-group-title">Групові витрати</p>
                <p className="trs-group-sub">Вам винні: 0.00 ₴ <span className="trs-dot">•</span> Ви винні: 0.00 ₴</p>
              </div>
            </div>

            <div className="trs-divider" />

            {ownCards.length > 0 && (
              <div className="trs-own">
                {ownCards.map((card) => (
                  <div className="trs-own-card" key={card.id} onClick={() => setOwnTarget(card)}>
                    <div className={`trs-own-icon trs-own-icon--${card.type}`}>
                      <span className="trs-own-mini">m</span>
                    </div>
                    <p className="trs-own-label">На мою {CARD_ACC[card.type] || card.name}</p>
                  </div>
                ))}
              </div>
            )}

            {favorites.length > 0 && (
              <div className="trs-block trs-section">
                <p className="trs-section-title">Обрані</p>
                <div className="trs-favs">
                  {favorites.map((c) => (
                    <div className={`trs-fav ${c.card ? 'trs-found' : ''}`} key={c.id} onClick={openContact(c)}>
                      <Avatar contact={c} size="lg" />
                      <p className="trs-fav-name">{c.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {cardDigits && !cardComplete && (
          <p className="trs-hint"><span className="trs-hint-emoji">✍️</span>Уведіть номер повністю</p>
        )}
        {cardComplete && cardMatch && (
          <div className="trs-block trs-section">
            <p className="trs-section-title">Знайдено</p>
            <div className="trs-contact trs-found" onClick={() => openAmount(cardMatch)}>
              <Avatar contact={cardMatch} size="md" />
              <p className="trs-contact-name">{cardMatch.name}</p>
            </div>
          </div>
        )}

        {showContacts && (
          <div className="trs-block trs-section">
            <div className="trs-section-head">
              <p className="trs-section-title">Контакти</p>
              {pickerAvailable && !q && contacts.length > 0 && (
                <button type="button" className="trs-add-more" onClick={addContacts} aria-label="Додати контакти">+</button>
              )}
            </div>
            {filtered.map((c) => (
              <div
                className={`trs-contact ${c.card ? 'trs-found' : ''}`}
                key={c.id}
                onClick={openContact(c)}
              >
                <Avatar contact={c} size="md" />
                <p className="trs-contact-name">{c.name}</p>
                <button
                  type="button"
                  className="trs-star"
                  onClick={(e) => { e.stopPropagation(); setContactFavorite(chatId, c.id, !c.favorite); }}
                  aria-label={c.favorite ? 'Прибрати з обраних' : 'Додати в обрані'}
                >
                  <Star filled={c.favorite} />
                </button>
              </div>
            ))}
            {contacts.length === 0 && (
              <button type="button" className="trs-access" onClick={addContacts} disabled={picking}>
                Дозволити доступ до контактів
              </button>
            )}
          </div>
        )}
      </div>
      {/* Номер картки: кнопка неактивна, доки не введено всі 16 цифр */}
      {cardDigits && (
        <button
          type="button"
          className="trs-send"
          style={keyboardInset ? { bottom: keyboardInset + 12 } : undefined}
          disabled={!cardComplete || cardMatch === undefined}
          onPointerDown={(e) => e.preventDefault()}
          onClick={() => openAmount(cardMatch)}
        >
          Переказати
        </button>
      )}
      {amountFor && (
        <AmountScreen
          recipient={amountFor.recipient}
          cardNumber={amountFor.cardNumber}
          cards={cards}
          fromCardId={fromCardId}
          onClose={() => setAmountFor(null)}
          onSent={(result) => { setSent(result); setAmountFor(null); }}
        />
      )}
      {sent && <TransferSuccessScreen transfer={sent} cards={cards} onDone={close} />}
      {ownTarget && (
        <OwnTransfer
          target={ownTarget}
          initialSource={cards.find((c) => c.id === fromCardId) || cards[0]}
          sources={cards.filter((c) => c.id !== ownTarget.id)}
          fxSpent={fxSpent}
          closing={closing}
          onBack={() => setOwnTarget(null)}
          onDone={close}
        />
      )}
    </div>
  ), document.body);
}
